import React, { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { IconSidebarDashboard } from "../icons/Icon_sidebar_dashboard";
import { IconSidebarBotList } from "../icons/Icon_sidebar_botlist";
import { IconSidebarPortfolio } from "../icons/Icon_sidebar_portfolio";
import { IconSidebarNotification } from "../icons/Icon_sidebar_notification";
import { IconSidebarLearn } from "../icons/Icon_sidebar_learn";
import { IconSidebarProfile } from "../icons/Icon_sidebar_profile";
import { IconSidebarSetting } from "../icons/Icon_sidebar_setting";
import { IconSidebarPaperTrading } from "../icons/Icon_sidebar_paper_trading";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";
import { IconSidebarTestingMode } from "../icons/Icon_sidebar_testing_mode.jsx";
import { ArchiveBoxIcon } from "@heroicons/react/24/outline";
import { baseAxios } from "../../apis/index.js";

export const NavigationMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const activeColor = "#F0F0FF";
  const inactiveColor = "#2D224E";

  const [isLearnOpen, setLearnOpen] = useState(false);
  const [isProductTourOpen, setProductTourOpen] = useState(false);
  const [learnSubItems, setLearnSubItems] = useState([]);
  const [isLoadingLearn, setIsLoadingLearn] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/learn")) {
      setLearnOpen(true);
    }
  }, [location.pathname]);

  // Fetch course categories from API
  useEffect(() => {
    const fetchCourseCategories = async () => {
      setIsLoadingLearn(true);
      try {
        const response = await baseAxios.get("/api/course-categories");

        // Transform API data to menu items format
        const formattedItems = response.data.map((category) => ({
          label: category.name || category.title,
          path: `/learn?category=${category.id}`,
          categoryId: category.id, // Store the category ID for comparison
        }));

        setLearnSubItems(formattedItems);
      } catch (error) {
        console.error("Error fetching course categories:", error);
        // Fallback to default item if API fails
        setLearnSubItems([{ label: "Trading Track", path: "/learn" }]);
      } finally {
        setIsLoadingLearn(false);
      }
    };

    fetchCourseCategories();
  }, []);

  const triggerDashboardTour = () => {
    navigate("/dashboard");
    setTimeout(() => {
      window.dispatchEvent(new Event("open-dashboard-tour"));
    }, 120);
  };

  const triggerBotListTour = () => {
    navigate("/bot-list");
    setTimeout(() => {
      window.dispatchEvent(new Event("open-botlist-tour"));
    }, 120);
  };

  const menuItems = [
    {
      renderIcon: (isActive) => (
        <IconSidebarDashboard color={isActive ? activeColor : inactiveColor} />
      ),
      label: "Dashboard",
      path: "/dashboard",
    },
    {
      renderIcon: (isActive) => (
        <IconSidebarBotList color={isActive ? activeColor : inactiveColor} />
      ),
      label: "Bot List",
      path: "/bot-list",
    },
    {
      renderIcon: (isActive) => (
        <IconSidebarPortfolio color={isActive ? activeColor : inactiveColor} />
      ),
      label: "Portfolio",
      path: "/portfolio",
    },
    {
      label: "Product Tour",
      renderIcon: (isActive) => <ArchiveBoxIcon className="w-5 h-5" />,
      isLearn: false,
      subItems: [
        { label: "Dashboard Tour", onClick: triggerDashboardTour },
        { label: "Bot List Tour", onClick: triggerBotListTour },
      ],
    },
    {
      renderIcon: (isActive) => (
        <IconSidebarLearn color={isActive ? activeColor : inactiveColor} />
      ),
      label: "Learn Center",
      basePath: "/",
      isLearn: true,
      subItems: learnSubItems,
    },
    {
      renderIcon: (isActive) => (
        <IconSidebarTestingMode
          color={isActive ? activeColor : inactiveColor}
        />
      ),
      label: "Testing Mode",
      path: "/testing-mode",
    },
    {
      renderIcon: (isActive) => (
        <IconSidebarNotification
          color={isActive ? activeColor : inactiveColor}
        />
      ),
      label: "Notification",
      path: "/notification",
    },
    {
      renderIcon: (isActive) => (
        <IconSidebarProfile color={isActive ? activeColor : inactiveColor} />
      ),
      label: "Account",
      path: "/account",
    },
  ];

  return (
    <nav className="flex flex-col w-full mt-7">
      {menuItems.map((item, index) => {
        // ---- Submenu handler (Learn + Product Tour) ----
        if (item.subItems && !item.path) {
          const isThisLearn = item.isLearn;

          const isOpen = isThisLearn ? isLearnOpen : isProductTourOpen;
          const setOpen = isThisLearn ? setLearnOpen : setProductTourOpen;

          const isParentActive = isThisLearn
            ? location.pathname.startsWith("/learn")
            : false;

          const labelTextColor = isParentActive
            ? "text-white"
            : "text-[#2D224E]";

          return (
            <div key={index}>
              <div
                onClick={() => setOpen(!isOpen)}
                className={`flex items-center justify-between gap-3 px-4 py-3 border-b border-[#F2F4F8] transition cursor-pointer
                  ${
                    isParentActive ? "font-medium !bg-[#674EB1] rounded-lg" : ""
                  }
                  ${labelTextColor}`}
              >
                <div className="flex items-center gap-3">
                  {item.renderIcon?.(isParentActive)}
                  <span className="text-[15px] tracking-wide">
                    {item.label}
                  </span>
                </div>

                {isOpen ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </div>

              {isOpen && (
                <div className="flex flex-col pl-5 pr-2 py-2">
                  {isThisLearn && isLoadingLearn ? (
                    <div className="px-4 py-3 text-sm text-[#2D224E]">
                      Loading...
                    </div>
                  ) : item.subItems.length === 0 && isThisLearn ? (
                    <div className="px-4 py-3 text-sm text-[#2D224E]">
                      No courses available
                    </div>
                  ) : (
                    item.subItems.map((subItem, subIndex) => {
                      if (subItem.path) {
                        // For Learn Center items, check both pathname and category query param
                        const currentCategory = searchParams.get("category");
                        const isActive = isThisLearn
                          ? location.pathname.startsWith("/learn") &&
                            currentCategory === String(subItem.categoryId)
                          : location.pathname.startsWith(subItem.path);

                        return (
                          <Link
                            to={subItem.path}
                            key={subIndex}
                            className={`flex items-center gap-3 px-4 py-3 my-1 transition rounded-lg text-sm !no-underline
                              ${
                                isActive
                                  ? "font-medium !bg-[#674EB1] text-white"
                                  : "text-[#2D224E] hover:bg-gray-100"
                              }`}
                          >
                            {subItem.label}
                          </Link>
                        );
                      }

                      return (
                        <button
                          key={subIndex}
                          type="button"
                          onClick={() => {
                            subItem.onClick?.();
                            setOpen(false);
                          }}
                          className={`flex items-center gap-3 px-4 py-3 border-b border-[#F2F4F8] transition hover:rounded-lg !bg-transparent !no-underline text-[#2D224E] hover:!text-[#2D224E] hover:!bg-gray-100`}
                        >
                          {subItem.label}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        }

        // ---- Normal menu item ----
        const isActive = item.path && location.pathname.startsWith(item.path);
        const labelTextColor = isActive ? "text-white" : "text-[#2D224E]";

        return (
          <Link
            to={item.path}
            key={index}
            className={`flex items-center gap-3 px-4 py-3 border-b border-[#F2F4F8] transition hover:rounded-lg hover:bg-[#EDEAF6] !no-underline
              ${isActive ? "font-medium !bg-[#674EB1] rounded-lg" : ""}
              ${labelTextColor}`}
          >
            {item.renderIcon?.(isActive)}
            <span className="text-[15px] tracking-wide">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};
